import { CommonModule } from '@angular/common';
import { Component, HostListener, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Subscription, firstValueFrom } from 'rxjs';
import { distinctUntilChanged, skip, switchMap } from 'rxjs/operators';
import { GoalsApiService, GoalDto, GoalTypeDto, CustomVariableDto } from '../../services/goals-api.service';
import { AuthService, SessionUser } from '../../services/auth.service';
import { NotificationService } from '../../services/notification.service';
import { isSandboxError } from '../../utils/api-error';
import { NotificationBackendService } from '../../services/notification-backend.service';
import { GoalView, GoalStatus } from './goal.types';
import { UserPermissions } from '../../services/permissions';
import { GoalsFormComponent } from './create/goals-form.component';
import { EntityOwnerService } from '../../services/entity-owner.service';
import { CreationStateService } from '../../services/creation-state.service';

@Component({
  selector: 'app-goals',
  standalone: true,
  imports: [CommonModule, FormsModule, TranslateModule, GoalsFormComponent],
  templateUrl: './goals.component.html',
  styleUrls: ['./goals.component.scss']
})
export class GoalsComponent implements OnInit, OnDestroy {
  private subscriptions = new Subscription();
  @ViewChild(GoalsFormComponent) goalFormComponent?: GoalsFormComponent;

  goals: GoalView[] = [];
  filteredGoals: GoalView[] = [];
  goalTypes: GoalTypeDto[] = [];
  customVariables: CustomVariableDto[] = [];

  readonly statusColors: Record<GoalStatus, string> = {
    active: '#22c55e',
    inactive: '#9ca3af'
  };

  readonly availableStatuses: { id: GoalStatus; labelKey: string }[] = [
    { id: 'active', labelKey: 'goals.filters.status.active' },
    { id: 'inactive', labelKey: 'goals.filters.status.inactive' }
  ];

  isLoading = false;
  loadingError: string | null = null;
  searchTerm = '';
  statusFilters: Record<GoalStatus, boolean> = this.createStatusFilters();
  pageSizeOptions = [10, 25, 50];
  pageSize = 10;
  currentPage = 0;
  panelOpen = false;
  formMode: 'create' | 'edit' = 'create';
  currentUser: SessionUser | null = null;
  readonly locale = 'it-IT';
  private ownerLabels = new Map<string, string>();
  /**
   * Goal per cui abbiamo appena lanciato un'estrazione. Lo stato durevole vive sulla riga
   * (IsExtracting dall'API) ed e' lui l'autorita'; questo set copre solo i pochi istanti fra
   * il nostro trigger e la scrittura lato server, altrimenti un reload immediato mostrerebbe
   * la riga sbloccata mentre l'API sta gia' rifiutando gli edit con un 409.
   */
  private locallyExtracting = new Set<string>();
  /**
   * Goal in attesa dei numeri (TNCIDs/Events/trend) dopo un'estrazione riuscita: guida lo
   * spinner nelle celle corrispondenti. Un Set indipendente dagli oggetti GoalView perche'
   * loadGoals() li ricostruisce da zero a ogni reload — un campo sull'oggetto non
   * sopravvivrebbe al giro successivo.
   */
  awaitingFreshData = new Set<string>();
  /** Timer del prossimo tentativo di poll per goal, per poterlo cancellare se serve. */
  private pendingDataPolls = new Map<string, ReturnType<typeof setTimeout>>();
  /**
   * Attese crescenti fra un tentativo e il successivo: i dati dipendono da un caricamento
   * GCS->MySQL esterno la cui durata non e' verificabile da qui (vedi la nota su
   * notification-backend.service.ts), quindi si comincia presto e ci si dirada. Oltre
   * l'ultimo tentativo si rinuncia in silenzio: arriveranno al prossimo reload manuale o
   * al giro notturno.
   */
  private static readonly DATA_POLL_DELAYS_MS = [5000, 10000, 20000, 40000, 80000];
  activeActionMenuId: string | null = null;
  activeActionItem: GoalView | null = null;
  actionMenuPosition = { top: 0, left: 0 };
  actionMenuArrowOffset = 24;
  actionMenuPlacement: 'below' | 'above' = 'below';
  recalculateModalOpen = false;
  recalculateModalTarget: GoalView | null = null;
  deleteModalOpen = false;
  deleteModalTarget: GoalView | null = null;

  // Advanced (Console/admin) mode — set from route data on the /console/goals alias. Unlocks the
  // full set of goal types and the raw Rule editor in the form. Defaults to the simplified user mode.
  advanced = false;

  constructor(
    private goalsApi: GoalsApiService,
    private authService: AuthService,
    private translate: TranslateService,
    private notificationService: NotificationService,
    private notificationBackendService: NotificationBackendService,
    private entityOwnerService: EntityOwnerService,
    private creationStateService: CreationStateService,
    private route: ActivatedRoute
  ) {}

  ngOnInit(): void {
    this.advanced = this.route.snapshot.data['advanced'] === true;
    this.subscriptions.add(
      this.authService.currentUser$.subscribe(user => {
        this.currentUser = user;
      })
    );
    this.subscriptions.add(
      this.entityOwnerService.owners$.subscribe(map => {
        this.ownerLabels = map;
        if (this.goals.length) {
          this.goals = this.goals.map(g => ({
            ...g,
            entity: this.resolveOwnerLabel(g.entityId, g.entityType)
          }));
          this.applyFilters();
        }
      })
    );
    this.subscriptions.add(
      this.authService.selectedDcr$
        .pipe(distinctUntilChanged(), skip(1), switchMap(() => this.authService.waitForEntitySync()))
        .subscribe(() => this.loadGoalTypes())
    );
    this.authService.waitForEntitySync().subscribe(() => this.loadGoalTypes());
    // A estrazione conclusa il lock si toglie subito, dal payload della notifica stessa
    // (extract.ts lo scrive su MySQL prima di notificare, quindi e' gia' noto per certo:
    // niente attesa di un reload solo per sapere che isExtracting e' tornato false).
    // loadGoals() resta come tentativo best-effort per il resto (TNCIDs/Events/trend),
    // che dipende da un caricamento GCS->MySQL esterno con tempistica non verificabile da
    // qui: potrebbe non essere ancora arrivato, ma non c'e' modo di saperlo in anticipo.
    // Vale anche per le estrazioni lanciate da un altro utente sulla stessa entity, che
    // arrivano sullo stesso canale.
    this.subscriptions.add(
      this.notificationBackendService.goalExtractionCompleted$.subscribe(({ goalId, isExtracting, isExtractionStalled, succeeded }) => {
        this.locallyExtracting.delete(goalId);
        this.applyExtractionLock(goalId, isExtracting, isExtractionStalled);
        this.loadGoals();
        // Solo sul successo: su un fallimento non c'e' alcun dato nuovo in arrivo da attendere.
        if (succeeded) {
          this.pollForFreshData(goalId);
        }
      })
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
    // setTimeout non e' una Subscription: this.subscriptions non lo cancella da solo.
    // Senza questo, lasciando la pagina a meta' di un poll, il timer scatterebbe comunque
    // e proverebbe a scrivere su un componente gia' distrutto.
    this.pendingDataPolls.forEach(handle => clearTimeout(handle));
    this.pendingDataPolls.clear();
    // Ensure entity selector is always re-enabled when component is destroyed
    this.creationStateService.setCreationMode(false);
  }

  @HostListener('document:click', ['$event'])
  handleDocumentClick(event: MouseEvent): void {
    const target = event.target instanceof HTMLElement ? event.target : null;
    const clickedMenu = !!target?.closest('.goals__action-menu');
    const clickedTrigger = !!target?.closest('.goals__action-trigger');
    if (!clickedMenu && !clickedTrigger) {
      this.closeActionMenu();
    }
  }

  loadGoalTypes(): void {
    const typesSub = this.goalsApi.getGoalTypes().subscribe({
      next: (types) => {
        this.goalTypes = [...types].sort((a, b) => {
          const nameA = a?.TypeName || '';
          const nameB = b?.TypeName || '';
          return nameA.localeCompare(nameB);
        });
        this.loadGoals();
      },
      error: (error) => {
        console.error('[goals] type load failed', error);
        this.goalTypes = [];
        this.loadGoals();
      }
    });
    this.subscriptions.add(typesSub);

    const variablesSub = this.goalsApi.getCustomVariables().subscribe({
      next: (variables) => {
        // Add "Any" option at the beginning
        this.customVariables = [
          { Name: '__ANY__' },
          ...variables
        ];
      },
      error: (error) => {
        console.error('[goals] custom variables load failed', error);
        this.customVariables = [{ Name: '__ANY__' }];
      }
    });
    this.subscriptions.add(variablesSub);
  }

  loadGoals(): void {
    this.isLoading = true;
    this.loadingError = null;
    const sub = this.goalsApi.getGoals().subscribe({
      next: (list) => {
        // Dedup by ID: a fan-out in the backend join (e.g. more than one trend row for the
        // same goal) must not turn into a duplicated row in the table. Last one wins.
        const byId = new Map<string, GoalView>();
        for (const dto of list) {
          const goal = this.mapGoal(dto);
          byId.set(goal.id, goal);
        }
        const mapped = Array.from(byId.values());
        this.entityOwnerService.ensureOwners(mapped.map(g => ({ id: g.entityId })));
        this.goals = this.filterGoalsByDcr(mapped).map(g => ({
          ...g,
          entity: this.resolveOwnerLabel(g.entityId, g.entityType)
        }));
        this.resetPagination();
        this.applyFilters();
        this.isLoading = false;
      },
      error: (error) => {
        console.error('[goals] load failed', error);
        this.loadingError = error?.message || this.translate.instant('goals.errors.load');
        this.goals = [];
        this.filteredGoals = [];
        this.isLoading = false;
      }
    });
    this.subscriptions.add(sub);
  }

  applyFilters(): void {
    const term = this.searchTerm.trim().toLowerCase();
    this.filteredGoals = this.goals.filter(goal => {
      const matchesTerm =
        !term ||
        goal.description.toLowerCase().includes(term) ||
        goal.typeName.toLowerCase().includes(term) ||
        goal.entity.toLowerCase().includes(term);
      const matchesStatus = this.statusFilters[goal.status];
      return matchesTerm && matchesStatus;
    });
    this.ensureValidPage();
  }

  toggleStatusFilter(status: GoalStatus): void {
    this.statusFilters = {
      ...this.statusFilters,
      [status]: !this.statusFilters[status]
    };
    this.resetPagination();
    this.applyFilters();
  }

  get statusCounts(): Record<GoalStatus, number> {
    const normalizedSearch = this.searchTerm.toLowerCase().trim();
    const counts: Record<GoalStatus, number> = {
      active: 0,
      inactive: 0
    };
    for (const goal of this.goals) {
      if (this.matchesSearch(goal, normalizedSearch)) {
        counts[goal.status] += 1;
      }
    }
    return counts;
  }

  get hasGlobalDataScope(): boolean {
    return this.authService.hasGlobalDataScope();
  }

  /**
   * Chi puo' scrivere sui goal. La rotta Engine di questa pagina e' gated su reach.audience
   * perche' la lettura e' concessa di proposito (le audience referenziano i goal), ma tutte
   * le scritture lato API vogliono reach.goal oppure admin_dashboard.all: senza questo getter
   * la UI offrirebbe azioni che il server rifiuta. `all` passa da solo, e' trasversale.
   */
  get canManageGoals(): boolean {
    return this.authService.hasPermission(UserPermissions.REACH_GOAL)
      || this.authService.hasPermission(UserPermissions.ADMIN_DASHBOARD);
  }

  get ownerEntityOptions(): { value: string; label: string; firstPartyGroupID?: string | null }[] {
    const dcrs = this.currentUser?.availableDCRs ?? [];
    return dcrs.map(dcr => ({
      value: dcr.id,
      label: dcr.Name || dcr.name || dcr.id,
      firstPartyGroupID: dcr.firstPartyGroupID
    }));
  }

  private matchesSearch(goal: GoalView, normalizedSearch: string): boolean {
    if (!normalizedSearch) {
      return true;
    }
    const haystack = `${goal.description} ${goal.typeName} ${goal.entity}`.toLowerCase();
    return haystack.includes(normalizedSearch);
  }

  private createStatusFilters(): Record<GoalStatus, boolean> {
    return {
      active: true,
      inactive: false
    };
  }

  onSearch(term: string): void {
    this.searchTerm = term;
    this.resetPagination();
    this.applyFilters();
  }


  get paginatedGoals(): GoalView[] {
    const startIndex = this.currentPage * this.pageSize;
    return this.filteredGoals.slice(startIndex, startIndex + this.pageSize);
  }

  get totalPages(): number {
    if (!this.filteredGoals.length) {
      return 1;
    }
    return Math.max(1, Math.ceil(this.filteredGoals.length / this.pageSize));
  }

  changePageSize(size: Event | string | number): void {
    let numeric: number;
    if (size instanceof Event) {
      const target = size.target as HTMLSelectElement | null;
      numeric = target ? parseInt(target.value, 10) : this.pageSize;
    } else if (typeof size === 'string') {
      numeric = parseInt(size, 10);
    } else {
      numeric = size;
    }

    if (!Number.isFinite(numeric) || numeric <= 0 || numeric === this.pageSize) {
      return;
    }
    this.pageSize = numeric;
    this.resetPagination();
    this.applyFilters();
  }

  goToPage(page: number): void {
    if (page < 0 || page >= this.totalPages) {
      return;
    }
    this.currentPage = page;
  }

  openCreatePanel(): void {
    this.closeActionMenu();
    this.formMode = 'create';
    this.panelOpen = true;
    // Notify sidebar to disable entity selector
    this.creationStateService.setCreationMode(true);
    setTimeout(() => this.goalFormComponent?.openCreate());
  }

  openEditPanel(item: GoalView): void {
    this.closeActionMenu();
    this.formMode = 'edit';
    this.panelOpen = true;
    // Notify sidebar to disable entity selector
    this.creationStateService.setCreationMode(true);
    setTimeout(() => this.goalFormComponent?.openEdit(item));
  }

  closePanel(): void {
    this.panelOpen = false;
    // Notify sidebar to enable entity selector
    this.creationStateService.setCreationMode(false);
  }

  handleFormSaved(extractingGoalId: string | null = null): void {
    if (extractingGoalId) {
      this.locallyExtracting.add(extractingGoalId);
      // Un'estrazione precedente sullo stesso goal potrebbe avere ancora un poll in corso:
      // quello vecchio non deve interferire con (o risolversi durante) questa nuova run.
      this.clearDataPollTimer(extractingGoalId);
      this.awaitingFreshData.delete(extractingGoalId);
    }
    this.panelOpen = false;
    // Notify sidebar to enable entity selector
    this.creationStateService.setCreationMode(false);
    this.loadGoals();
  }

  openDeleteModal(item: GoalView): void {
    this.deleteModalTarget = item;
    this.deleteModalOpen = true;
    this.closeActionMenu();
  }

  closeDeleteModal(): void {
    this.deleteModalOpen = false;
    this.deleteModalTarget = null;
  }

  async confirmDelete(): Promise<void> {
    if (!this.deleteModalTarget) {
      return;
    }
    const item = this.deleteModalTarget;
    this.closeDeleteModal();
    try {
      await firstValueFrom(this.goalsApi.deleteGoal(item.id));
      this.notificationService.notify('success', this.translate.instant('goals.messages.deleted'));
      this.loadGoals();
    } catch (error) {
      console.error('[goals] delete failed', error);
      this.notificationService.notify('error', (error as Error)?.message || this.translate.instant('goals.errors.delete'));
    }
  }

  // Deprecated: use openDeleteModal instead
  async deleteGoal(item: GoalView): Promise<void> {
    this.openDeleteModal(item);
  }

  openRecalculateModal(item: GoalView): void {
    this.recalculateModalTarget = item;
    this.recalculateModalOpen = true;
    this.closeActionMenu();
  }

  closeRecalculateModal(): void {
    this.recalculateModalOpen = false;
    this.recalculateModalTarget = null;
  }

  /**
   * L'endpoint di ricalcolo resta in attesa per tutta l'estrazione BigQuery: fa il poll del
   * job prima di rispondere, per non farsi throttlare la CPU da Cloud Run a metà (vedi
   * settings/goals/extract.ts). Attenderne la risposta terrebbe la modale aperta per minuti,
   * quindi la lanciamo senza await: la modale si chiude appena l'estrazione è partita e
   * l'esito — completata o fallita — arriva come notifica dal notification center.
   */
  performRecalculate(): void {
    if (!this.recalculateModalTarget) {
      return;
    }
    const goalId = this.recalculateModalTarget.id;
    this.closeRecalculateModal();
    this.notificationService.notify('info', this.translate.instant('goals.messages.recalculating'));
    this.locallyExtracting.add(goalId);
    this.applyExtractionLock(goalId, true);
    // Idem: un poll rimasto da un ricalcolo precedente non deve confondersi con questo.
    this.clearDataPollTimer(goalId);
    this.awaitingFreshData.delete(goalId);
    this.goalsApi.recalculateGoal(goalId).subscribe({
      error: (error) => {
        // L'estrazione non e' partita: togliere subito il lock, altrimenti la riga resterebbe
        // bloccata fino alla scadenza della guardia lato server senza motivo.
        this.locallyExtracting.delete(goalId);
        this.applyExtractionLock(goalId, false);
        console.error('[goals] recalculate failed', error);
        this.notificationService.notify('error', isSandboxError(error)
            ? this.translate.instant('errors.sandboxEntity')
            : (error as Error)?.message || this.translate.instant('goals.errors.recalculate'));
      }
    });
  }

  async toggleActivation(item: GoalView): Promise<void> {
    const nextStatus: GoalStatus = item.status === 'active' ? 'inactive' : 'active';
    const apiStatus = nextStatus === 'active' ? 1 : 0;
    try {
      await firstValueFrom(this.goalsApi.updateGoal({ ID: item.id, Status: apiStatus }));
      item.status = nextStatus;
      const messageKey = nextStatus === 'active' ? 'goals.messages.activated' : 'goals.messages.deactivated';
      this.notificationService.notify('success', this.translate.instant(messageKey, { name: item.description }));
      this.applyFilters();
    } catch (error) {
      console.error('[goals] status toggle failed', error);
      this.notificationService.notify('error', this.translate.instant('goals.errors.status'));
    }
    this.closeActionMenu();
  }

  toggleActionsMenu(item: GoalView, event: Event): void {
    event.stopPropagation();
    const isOpen = this.activeActionMenuId === item.id;
    if (isOpen) {
      this.closeActionMenu();
      return;
    }
    this.activeActionMenuId = item.id;
    this.activeActionItem = item;
    this.updateActionMenuPosition(event.currentTarget as HTMLElement);
  }

  clearSearch(): void {
    if (!this.searchTerm) {
      return;
    }
    this.searchTerm = '';
    this.resetPagination();
    this.applyFilters();
  }


  /**
   * Riflette il lock sulla riga gia' in pagina, senza aspettare un reload della lista.
   * `stalled` e' opzionale: al trigger di un ricalcolo (performRecalculate) non lo tocca,
   * per non cancellare un avviso di stallo genuino mentre riparte una nuova estrazione.
   */
  private applyExtractionLock(goalId: string, extracting: boolean, stalled?: boolean): void {
    const goal = this.goals.find(g => g.id === goalId);
    if (goal) {
      goal.isExtracting = extracting;
      if (stalled !== undefined) {
        goal.isExtractionStalled = stalled;
      }
    }
  }

  // ── Poll dei numeri post-estrazione (TNCIDs/Events/trend) ───────────────────────────
  //
  // Il completamento dell'estrazione non garantisce che questi dati siano gia' leggibili:
  // passano da un caricamento GCS->MySQL esterno a questa API (vedi la nota su
  // notification-backend.service.ts) la cui tempistica da qui non e' verificabile.
  // Si ritenta con attese crescenti finche' non compaiono o si esauriscono i tentativi.

  private pollForFreshData(goalId: string, attempt = 0): void {
    this.clearDataPollTimer(goalId);
    this.awaitingFreshData.add(goalId);
    const delay = GoalsComponent.DATA_POLL_DELAYS_MS[attempt];
    const handle = setTimeout(() => this.checkForFreshData(goalId, attempt), delay);
    this.pendingDataPolls.set(goalId, handle);
  }

  private checkForFreshData(goalId: string, attempt: number): void {
    this.goalsApi.getGoal(goalId).subscribe({
      next: (dto) => {
        this.pendingDataPolls.delete(goalId);
        // Details non nullo e' lo stesso segnale che governa le "-" in tabella (mapGoal):
        // significa che 07_frontend_static.count_goals_trend ha finalmente una riga per
        // questo goal, cioe' che il caricamento esterno ha fatto in tempo.
        if (dto?.Details != null) {
          this.patchGoalRow(dto);
          this.awaitingFreshData.delete(goalId);
          return;
        }
        const nextAttempt = attempt + 1;
        if (nextAttempt < GoalsComponent.DATA_POLL_DELAYS_MS.length) {
          this.pollForFreshData(goalId, nextAttempt);
        } else {
          // Rinuncia in silenzio: nessun errore da segnalare, solo un'attesa piu' lunga
          // del previsto. I numeri arriveranno al prossimo reload manuale o al giro notturno.
          this.awaitingFreshData.delete(goalId);
        }
      },
      error: (error) => {
        console.error('[goals] data poll failed', error);
        this.pendingDataPolls.delete(goalId);
        this.awaitingFreshData.delete(goalId);
      }
    });
  }

  private clearDataPollTimer(goalId: string): void {
    const handle = this.pendingDataPolls.get(goalId);
    if (handle !== undefined) {
      clearTimeout(handle);
      this.pendingDataPolls.delete(goalId);
    }
  }

  /** Sostituisce la riga in memoria con dati freschi per un singolo goal, senza un reload completo. */
  private patchGoalRow(dto: GoalDto): void {
    const index = this.goals.findIndex(g => g.id === dto.ID);
    if (index === -1) {
      // La riga non c'e' piu' in pagina (cancellata, o filtrata via nel frattempo): niente da fare.
      return;
    }
    const refreshed = this.mapGoal(dto);
    this.goals[index] = { ...refreshed, entity: this.resolveOwnerLabel(refreshed.entityId, refreshed.entityType) };
    this.applyFilters();
  }

  private resetPagination(): void {
    this.currentPage = 0;
  }

  private ensureValidPage(): void {
    if (!this.filteredGoals.length) {
      this.currentPage = 0;
      return;
    }
    const totalPages = Math.max(1, Math.ceil(this.filteredGoals.length / this.pageSize));
    const lastPageIndex = Math.max(0, totalPages - 1);
    if (this.currentPage > lastPageIndex) {
      this.currentPage = lastPageIndex;
    }
  }

  private mapGoal(dto: GoalDto): GoalView {
    const goalType = this.goalTypes.find(t => t.Type === dto.Type);
    let totalTNCIDs: number | undefined;
    let totalEvents: number | undefined;
    let trend: { date: string; events: number; tncids: number }[] | undefined;

    // Parse Details JSON to extract the trend summary (totals + per-day series).
    if (dto.Details) {
      try {
        const details = JSON.parse(dto.Details);
        totalTNCIDs = details.TotalTNCIDs != null ? Number(details.TotalTNCIDs) : undefined;
        totalEvents = details.TotalEvents != null ? Number(details.TotalEvents) : undefined;
        if (Array.isArray(details.data)) {
          trend = details.data.map((point: { CreationDate?: string; Events?: number; TNCIDs?: number }) => ({
            date: point?.CreationDate ?? '',
            events: Number(point?.Events) || 0,
            tncids: Number(point?.TNCIDs) || 0
          }));
        }
      } catch (e) {
        console.error('[goals] failed to parse Details JSON', e);
      }
    }

    return {
      id: dto.ID,
      entityType: dto.EntityType,
      entityId: dto.EntityID,
      entity: this.resolveOwnerLabel(dto.EntityID, dto.EntityType),
      showInDashboard: dto.ShowInDashboard,
      description: dto.Description,
      type: dto.Type,
      typeName: goalType?.TypeName || dto.Type,
      rule: dto.Rule,
      isRegExp: dto.IsRegExp,
      status: this.normalizeStatus(dto.Status),
      isExtracting: !!dto.IsExtracting || this.locallyExtracting.has(dto.ID),
      isExtractionStalled: !!dto.IsExtractionStalled,
      preview: dto.Preview,
      customVarName: dto.CustomVarName,
      customVarValue: dto.CustomVarValue,
      customVarOperator: dto.CustomVarOperator,
      totalTNCIDs: totalTNCIDs,
      totalEvents: totalEvents,
      trend: trend
    };
  }

  // ── Trend sparkline (advanced list only) ────────────────────────────────────

  /** SVG polyline points for a goal's per-day events series, scaled to a 100×28 viewBox. */
  sparklinePoints(goal: GoalView): string {
    const values = (goal.trend ?? []).map(p => p.events);
    if (!values.length) {
      return '';
    }
    const width = 100;
    const height = 28;
    const pad = 2;
    const max = Math.max(...values);
    const min = Math.min(...values);
    const range = max - min || 1;
    const n = values.length;
    return values.map((v, i) => {
      const x = n === 1 ? width / 2 : (i / (n - 1)) * (width - 2 * pad) + pad;
      const y = height - pad - ((v - min) / range) * (height - 2 * pad);
      return `${x.toFixed(1)},${y.toFixed(1)}`;
    }).join(' ');
  }

  hasTrend(goal: GoalView): boolean {
    return !!goal.trend && goal.trend.length > 0;
  }

  /** Download the per-day trend as CSV (mirrors the old admin "Download Trend Data" action). */
  downloadTrendCsv(goal: GoalView): void {
    this.closeActionMenu();
    const rows: (string | number)[][] = [['CreationDate', 'Events', 'TNCIDs']];
    for (const point of goal.trend ?? []) {
      rows.push([point.date, point.events, point.tncids]);
    }
    const csv = rows.map(r => r.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${goal.description || 'goal'} - Trend.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  }

  private normalizeStatus(status: number): GoalStatus {
    return status === 1 ? 'active' : 'inactive';
  }

  private resolveOwnerLabel(entityId?: string | null, entityType?: string | null): string {
    const normalizedId = (entityId ?? '').toString().trim();
    const key = this.buildOwnerKey(entityType, entityId);
    if (key) {
      const mapped = this.ownerLabels.get(key);
      if (mapped) {
        return mapped;
      }
    }
    // EntityOwnerService always stores as publisher::id regardless of actual entityType
    if (normalizedId) {
      const publisherKey = `publisher::${normalizedId}`;
      const mapped = this.ownerLabels.get(publisherKey);
      if (mapped) {
        return mapped;
      }
    }
    // Check ownerEntityOptions directly (populated from currentUser.availableDCRs)
    if (normalizedId) {
      const option = this.ownerEntityOptions.find(o => o.value === normalizedId);
      if (option) {
        return option.label;
      }
    }
    if (normalizedId) {
      const friendly = this.authService.getDcrNameById(normalizedId);
      if (friendly && friendly !== normalizedId) {
        return friendly;
      }
    }
    if (normalizedId && entityType) {
      return `${entityType} • ${normalizedId}`;
    }
    return normalizedId || entityType || '-';
  }

  private filterGoalsByDcr(goals: GoalView[]): GoalView[] {
    const filterId = this.getActiveDcrFilter();
    if (!filterId) {
      return goals;
    }
    return goals.filter(goal => this.normalizeEntityId(goal.entityId) === filterId);
  }

  /** Single source of truth for the entity-selector scope (see AuthService.getEntityScopeFilterId). */
  private getActiveDcrFilter(): string | null {
    return this.authService.getEntityScopeFilterId();
  }

  private normalizeEntityId(value?: string | null): string {
    return (value ?? '').toString().trim().toLowerCase();
  }

  private buildOwnerKey(entityType?: string | null, entityId?: string | null): string | null {
    const type = (entityType ?? '').toString().trim().toLowerCase();
    const id = (entityId ?? '').toString().trim();
    if (!type || !id) {
      return null;
    }
    return `${type}::${id}`;
  }

  private closeActionMenu(): void {
    this.activeActionMenuId = null;
    this.activeActionItem = null;
    this.actionMenuArrowOffset = 24;
    this.actionMenuPlacement = 'below';
  }

  private updateActionMenuPosition(trigger: HTMLElement): void {
    if (typeof window === 'undefined') {
      this.actionMenuPosition = { top: 0, left: 0 };
      this.actionMenuArrowOffset = 24;
      return;
    }
    const rect = trigger.getBoundingClientRect();
    const menuWidth = 240;
    const estimatedMenuHeight = 220;
    const padding = 16;
    const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
    const viewportWidth = window.innerWidth || document.documentElement.clientWidth;

    const spaceBelow = viewportHeight - rect.bottom - padding;
    const spaceAbove = rect.top - padding;
    const openAbove = spaceBelow < estimatedMenuHeight && spaceAbove >= estimatedMenuHeight;
    this.actionMenuPlacement = openAbove ? 'above' : 'below';

    const preferredLeft = rect.right - menuWidth;
    const maxLeft = viewportWidth - menuWidth - padding;
    const left = Math.min(Math.max(preferredLeft, padding), maxLeft);

    this.actionMenuPosition = {
      top: openAbove ? rect.top - estimatedMenuHeight - 8 : rect.bottom + 8,
      left
    };

    const buttonCenter = rect.left + rect.width / 2;
    const arrowPadding = 24;
    const relativeCenter = buttonCenter - left;
    const clampedOffset = Math.min(Math.max(relativeCenter, arrowPadding), menuWidth - arrowPadding);
    this.actionMenuArrowOffset = clampedOffset;

    setTimeout(() => {
      const menu = document.querySelector('.goals__action-menu') as HTMLElement;
      if (!menu) return;
      const actualHeight = menu.offsetHeight;
      if (openAbove) {
        this.actionMenuPosition = { top: rect.top - actualHeight - 8, left };
      } else if (this.actionMenuPosition.top + actualHeight > viewportHeight - padding) {
        this.actionMenuPlacement = 'above';
        this.actionMenuPosition = { top: rect.top - actualHeight - 8, left };
      }
    }, 0);
  }

  formatVolume(volume: number | undefined): string {
    if (volume === undefined || volume === null) {
      return '-';
    }
    if (volume >= 1000000) {
      return (volume / 1000000).toFixed(1) + 'M';
    } else if (volume >= 1000) {
      return (volume / 1000).toFixed(1) + 'K';
    } else {
      return volume.toString();
    }
  }

}
